import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button component with kid-friendly variants for the school app.
 * Uses design tokens for consistent theming.
 * 
 * Note: will-change and transform-gpu force GPU layer promotion to fix
 * a Chrome Mac bug where :hover styles compute but don't paint after refresh.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-[15px] font-semibold ring-offset-background transition-[transform,background-color,color,opacity] duration-150 ease-out [-webkit-tap-highlight-color:transparent] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 active:scale-[0.97]",
  {
    variants: {
      variant: {
        // Default primary button
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        // Destructive/danger button
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Outline button
        outline: "border border-border bg-card text-foreground hover:bg-muted",
        // Secondary button
        secondary: "bg-primary/10 text-primary hover:bg-primary/15",
        // Ghost button (minimal)
        ghost: "text-foreground hover:bg-muted",
        // Link style button
        link: "text-primary underline-offset-4 hover:underline",
        // Teacher-themed button (sleek charcoal)
        teacher: "bg-teacher text-teacher-foreground hover:bg-teacher/90",
        // Student-themed button (cyan/blue)
        student: "bg-student text-primary-foreground hover:bg-student/90",
        // Success/positive action button (teacher charcoal for sign-in)
        success: "bg-teacher text-teacher-foreground hover:bg-teacher/90",
        // Warning button (orange)
        warning: "bg-grade-needs-work text-primary-foreground hover:bg-grade-needs-work/90",
      },
      size: {
        default: "h-11 px-5 py-2",
        sm: "h-9 rounded-input px-3 text-sm",
        lg: "h-12 px-6 text-body",
        xl: "h-14 px-8 text-title3",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
